import * as React from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useFetchInvoice,
  useCreateInvoiceJustification,
  useAbonedInvoiceJustifications,
  useUpdateInvoiceCarrier,
  useUpdateInvoiceDeliveryDate,
} from 'hooks/api/invoices';
import { usePermission } from 'hooks/usePermission';
import { notify } from 'services/notifications';
import { Scaffold } from 'layouts';
import { Button } from 'components/ui/button';
import {
  ActionsMenu,
  type MenuAction,
} from 'pages/tracking-v2/shared-components';

import { useInvoiceStore, useInvoiceTravelInformationStore } from './hooks';
import {
  PageHeader,
  InvoiceTransportMap,
  InvoiceInfoBar,
  InvoiceTransportProgression,
  InvoiceCompaniesGrid,
  GeolocationsAndOccurrencesListDialog,
  type GeolocationsAndOccurrencesListDialogRef,
  CreateInvoiceJustificationDialog,
  type CreateInvoiceJustificationDialogRef,
  UpdateInvoiceCarrierDialog,
  type UpdateInvoiceCarrierDialogRef,
  UpdateInvoiceDeliveryDateDialog,
  type UpdateInvoiceDeliveryDateDialogRef,
  InvoiceItemsList,
  ActivitiesTimeline,
  JustificationsTimeline,
} from './components';
import { DeliveryVoucher } from './components/delivery-voucher';

export const InvoiceDetailsPage = () => {
  const { id: invoiceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = usePermission();
  const { setInvoice, resetInvoiceStoreState } = useInvoiceStore();
  const { setTravelInformation, resetInvoiceTravelInformationStoreState } =
    useInvoiceTravelInformationStore();
  const { fetchInvoice } = useFetchInvoice();
  const { createInvoiceJustification } = useCreateInvoiceJustification();
  const { updateInvoiceCarrier } = useUpdateInvoiceCarrier();
  const { updateInvoiceDeliveryDate } = useUpdateInvoiceDeliveryDate();
  const { abonedInvoiceJustifications } = useAbonedInvoiceJustifications();

  const geolocationsListDialogRef =
    React.useRef<GeolocationsAndOccurrencesListDialogRef>(null);
  const createInvoiceJustificationDialogRef =
    React.useRef<CreateInvoiceJustificationDialogRef>(null);
  const updateInvoiceCarrierDialogRef =
    React.useRef<UpdateInvoiceCarrierDialogRef>(null);
  const updateInvoiceDeliveryDateDialogRef =
    React.useRef<UpdateInvoiceDeliveryDateDialogRef>(null);

  const invoiceQuery = useQuery({
    queryKey: ['fetch-invoice', invoiceId],
    queryFn: () => fetchInvoice(invoiceId!),
    onSuccess: setInvoice,
    onError: (e: any) => {
      notify('error', e?.message);
    },
    refetchOnWindowFocus: false,
  });

  const createJustificationMutation = useMutation({
    mutationKey: ['create-invoice-justification'],
    mutationFn: createInvoiceJustification,
    onSuccess: async () => {
      createInvoiceJustificationDialogRef.current?.close();
      notify('success', 'Justificativa criada com sucesso!');
      await invoiceQuery.refetch();
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const updateCarrierMutation = useMutation({
    mutationKey: ['update-invoice-carrier'],
    mutationFn: updateInvoiceCarrier,
    onSuccess: async () => {
      updateInvoiceCarrierDialogRef.current?.close();
      notify('success', 'Transportadora alterada com sucesso!');
      await invoiceQuery.refetch();
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const updateDeliveryDateMutation = useMutation({
    mutationKey: ['update-invoice-delivery-date'],
    mutationFn: updateInvoiceDeliveryDate,
    onSuccess: async () => {
      updateInvoiceDeliveryDateDialogRef.current?.close();
      notify('success', 'Data de entrega alterada com sucesso!');
      await invoiceQuery.refetch();
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const abonedJustificationsMutation = useMutation({
    mutationKey: ['aboned-invoice-justifications'],
    mutationFn: (ids: number[]) => abonedInvoiceJustifications({ ids }),
    onSuccess: async () => {
      notify('success', 'Justificativa(s) abonada(s) com sucesso!');
      await invoiceQuery.refetch();
    },
    onError: (e: any) => {
      notify('error', e?.message);
    },
  });

  const handleNavigateBack = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const menuActions = React.useMemo((): MenuAction[] => {
    const invoice = invoiceQuery?.data;

    return [
      {
        label: 'Localizações',
        onClick: geolocationsListDialogRef.current?.open,
        disabled: !invoice,
      },
      {
        label: 'Adicionar justificativa',
        onClick: createInvoiceJustificationDialogRef.current?.open,
        disabled: !invoice,
      },
      ...((hasPermissionTo(['TRACKING.CHANGETRANSPORTER']) && [
        {
          label: 'Alterar transportadora',
          onClick: updateInvoiceCarrierDialogRef.current?.open,
          disabled: !invoice,
        },
      ]) ||
        []),
      ...((hasPermissionTo(['TRACKING.UPDATE_DELIVERY_DATE']) && [
        {
          label: 'Alterar data de entrega',
          onClick: updateInvoiceDeliveryDateDialogRef.current?.open,
          disabled: !invoice,
        },
      ]) ||
        []),
    ];
  }, [invoiceQuery?.data, hasPermissionTo]);

  React.useEffect(() => {
    return () => {
      resetInvoiceStoreState();
      resetInvoiceTravelInformationStoreState();
    };
  }, [resetInvoiceStoreState, resetInvoiceTravelInformationStoreState]);

  return (
    <Scaffold>
      <GeolocationsAndOccurrencesListDialog ref={geolocationsListDialogRef} />
      <CreateInvoiceJustificationDialog
        ref={createInvoiceJustificationDialogRef}
        onSubmit={createJustificationMutation.mutate}
        loading={createJustificationMutation.isLoading}
      />
      <UpdateInvoiceCarrierDialog
        ref={updateInvoiceCarrierDialogRef}
        onSubmit={updateCarrierMutation.mutate}
        loading={updateCarrierMutation.isLoading}
      />
      <UpdateInvoiceDeliveryDateDialog
        ref={updateInvoiceDeliveryDateDialogRef}
        onSubmit={updateDeliveryDateMutation.mutate}
        loading={updateDeliveryDateMutation.isLoading}
      />
      <PageHeader>
        <Button size={'sm'} variant={'secondary'} onClick={handleNavigateBack}>
          Voltar
        </Button>
      </PageHeader>
      <div className={'flex flex-col gap-6 mt-4'}>
        <div className={'flex flex-col'}>
          <InvoiceTransportMap
            onInvoiceTravelInformationChange={setTravelInformation}
          />
          <InvoiceInfoBar className={'z-10 -mt-3'}>
            <ActionsMenu menuActions={menuActions} />
          </InvoiceInfoBar>
        </div>
        <InvoiceTransportProgression />
        <InvoiceCompaniesGrid />
        <InvoiceItemsList />
        {!!invoiceQuery?.data?.deliveryDate && <DeliveryVoucher />}
        <JustificationsTimeline />
        <ActivitiesTimeline />
      </div>
    </Scaffold>
  );
};
